import { AdAccountPageType, CurrentBrand, FacebookBusinessManager, FBPixel, GeographicTargetingType, InstagramPost, PixelConversionEvent } from "modules/Types"
import { Dispatch, SetStateAction } from "react"
import Axios from "helpers/Interceptor"
import dayjs from "dayjs"
import { CONTINUE, NEXT, REVIEW, REVIEW_YOUR_CAMPAIGN, SAVE } from "./constants"
import { PreConnectionConfigs, UploadedAssetType } from "../utils/fbCampaigns"
import Logger from "Logger"

export enum STEPS {
  LINK = "link",
  CREATIVE = "creative",
  BUDGET = "budget",
  FINALIZATION = "review",
}

const { BUDGET, CREATIVE } = STEPS

const dev = process.env.NODE_ENV !== "production"

export enum Platforms {
  SPOTIFY = "spotify",
  ALL = "all",
  APPLE_MUSIC = "apple_music",
  YOUTUBE = "youtube",
}

type DataPlatform = {
  name: string
  key: string
  goal: string
  selected: boolean
  subtitle: string | null
  uploadingAssets: boolean
}

export type SelectedPlatforms = {
  spotify: DataPlatform
  all: DataPlatform
  apple_music: DataPlatform
  youtube: DataPlatform
}

export const getIfTargetingSaveButtonIsDisabled = (
  targets: GeographicTargetingType[],
  totalBudget: number
) => {
  const locations = targets.map(({ locations }) => Object.values(locations))
  const budgets = targets.map(({ budget }) => budget)
  return (
    totalBudget !== 1
    || !locations.filter((location) => location.length > 0).length
    || budgets.includes(0)
  )
}

export const getWhenCreativeTabNextButtonIsDisabled = (args: {
  adConnectionsAdded: boolean
  adConnectionsConfirmed: boolean
    selectedInstagramPost?: InstagramPost | null
  preConnectionConfigurations?: PreConnectionConfigs
  selectedCreativeType: string
  arePagesSelected: boolean
  uploadingAssets: boolean
  uploadedImages: UploadedAssetType[] | null;
  cta: string | { value: string; label: string }
  caption: string
}) => {
  const {
    uploadedImages,
    adConnectionsAdded,
    adConnectionsConfirmed,
    uploadingAssets,
    selectedInstagramPost,
    arePagesSelected,
    preConnectionConfigurations,
    selectedCreativeType,
    cta,
  } = args

  if (!adConnectionsAdded) {
    return !(
        arePagesSelected &&
        preConnectionConfigurations &&
        preConnectionConfigurations.status
      )
  }

  if (!adConnectionsConfirmed) {
    return false
  }

  return selectedCreativeType === "instagram_posts"
    ? !(arePagesSelected && selectedInstagramPost && cta)
    : !(
        !uploadingAssets &&
        arePagesSelected &&
        uploadedImages &&
        uploadedImages.length > 0 &&
        cta
      )
}

export const getNewCampaignName = (currentLink: string | URL) => {
  try {
    const url = new URL(currentLink)

    if (url) {
      return `${url.hostname} Campaign - ${dayjs().format("MM/DD")}`
    }
  } catch (e) {
    console.log("Not valid link yet - ", e)
    return `Link Clicks Campaign - ${dayjs().format("MM/DD")}`
  }
}

export const getNextButtonLabel = (args: {
  showTargetingView: boolean
  selectedTab?: STEPS | null
  isNextButtonMobile: boolean
}) => {
  const {
    showTargetingView,
    selectedTab,
    isNextButtonMobile,
  } = args

  if (showTargetingView) return SAVE
  if (selectedTab === BUDGET && isNextButtonMobile) return REVIEW
  if (selectedTab === BUDGET && !isNextButtonMobile) return REVIEW_YOUR_CAMPAIGN
  if (selectedTab === CREATIVE) return CONTINUE
  return NEXT
}

export const getNextButtonWidth = (args: {
  selectedTab?: STEPS | null
  isNextButtonResponsive: boolean
  isNextButtonMobile: boolean
}) => {
  const { selectedTab, isNextButtonMobile, isNextButtonResponsive } = args

  if (selectedTab === BUDGET && isNextButtonMobile) return undefined
  if (selectedTab === BUDGET && !isNextButtonMobile && isNextButtonResponsive)
    return "220px"
  if (selectedTab === BUDGET) return "300px"
  return undefined
}

export const convertArrayDraftDataToSendAsObject = (
  data: Record<string, unknown>[]
) => {
  const [
    currentLink,
    selectedFB_page,
    selectedFBAdAccount,
    selectedInstaPage,
    uploadedImages,
    selectedInstagramPost,
    caption,
    budgetValue,
    startDate,
    endDate,
    targets,
    audiences,
    customAudiences,
    additionalPageAudiences,
    selectedTab,
    cta,
    campaignName,
    minAge,
    maxAge,
    gender,
    languages,
    addVisualSelected,
    conversionsEnabled,
    conversionsEventName,
    facebook_pixel,
    facebook_business_manager,
    logged_in_fb_user,
  ] = data

  return {
    currentLink,
    selectedFB_page,
    selectedFBAdAccount,
    selectedInstaPage,
    uploadedImages,
    selectedInstagramPost,
    caption,
    budgetValue,
    startDate,
    endDate,
    targets,
    audiences,
    customAudiences,
    additionalPageAudiences,
    selectedTab,
    cta,
    campaignName,
    demographics: {
      age: {
        min: minAge,
        max: maxAge,
      },
      gender,
      languages,
    },
    addVisualSelected,
    conversionsEnabled,
    conversionsEventName,
    facebook_pixel,
    facebook_business_manager,
    logged_in_fb_user,
  }
}

export const getIfDraftHasConversionsEnabled = (data: {
  campaignDraftData: Record<string, unknown> | null,
}) => {
  const { campaignDraftData } = data
  if  (!campaignDraftData) return false

  const draftMetadata = campaignDraftData?.campaign_metadata as { campaign_input: { brand: CurrentBrand } }
  const draftConnections = draftMetadata?.campaign_input.brand.connections
  if (!draftConnections?.facebook_ad_account) return false

  const campaignDraftMetadata = campaignDraftData.campaign_metadata as { campaign_input: { conversionsEnabled: boolean } }
  return Boolean(campaignDraftMetadata.campaign_input.conversionsEnabled)
}

export const getConversionsEvents = async () => {
  try {
    const response = await Axios.get('/brand/conversions/events')
    const events: PixelConversionEvent[] = response.data
    return events
  } catch (error) {
    Logger.error(error)
  }
}
